import { routes } from '../../assets/constants';
import AuthClientStore from '../services/auth-client.service';
import { ApiMethod } from '../models/api-types.type';
import { useAuthContext } from './use-auth-context.hook';
import {
    SocialAccount,
    SocialAccountsListResponse,
    SocialPost,
    SocialPostsListResponse,
    SocialPostMetric,
    SocialDashboardMetrics,
    ConnectSocialAccountPayload,
    UpdateSocialAccountPayload,
    CreateSocialPostPayload,
    UpdateSocialPostPayload,
    GenerateCaptionPayload,
} from '../models/social-scheduler.model';

/**
 * Social Media Scheduler API hook — mirrors the pms-api-v2
 * pms-social-scheduler controller. Uses `sendAuthGuardedRequest`
 * so 401s auto-refresh the access token.
 */
export const useSocialSchedulerApi = () => {
    const { sendAuthGuardedRequest } = useAuthContext();

    // ───────────────────────────────────────────────────────────
    // Accounts
    // ───────────────────────────────────────────────────────────

    const listAccounts = async (params?: {
        platform?: string;
        status?: string;
        page?: number;
        limit?: number;
    }): Promise<SocialAccountsListResponse> => {
        const qs = new URLSearchParams();
        if (params?.platform) qs.set('platform', params.platform);
        if (params?.status) qs.set('status', params.status);
        if (params?.page) qs.set('page', String(params.page));
        if (params?.limit) qs.set('limit', String(params.limit));
        const suffix = qs.toString() ? `?${qs.toString()}` : '';
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.socialScheduler.listAccounts + suffix,
        );
        return response?.data || response;
    };

    const getAccount = async (uuid: string): Promise<SocialAccount> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.socialScheduler.getAccount(uuid),
        );
        return response?.data || response;
    };

    const connectAccount = async (
        payload: ConnectSocialAccountPayload,
    ): Promise<SocialAccount> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.POST,
            routes.socialScheduler.connectAccount,
            payload,
        );
        return response?.data || response;
    };

    const updateAccount = async (
        uuid: string,
        payload: UpdateSocialAccountPayload,
    ): Promise<SocialAccount> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.PATCH,
            routes.socialScheduler.updateAccount(uuid),
            payload,
        );
        return response?.data || response;
    };

    const disconnectAccount = async (
        uuid: string,
    ): Promise<{ success: boolean; postsDeleted?: number }> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.DELETE,
            routes.socialScheduler.disconnectAccount(uuid),
        );
        return response?.data || response;
    };

    const getAffectedPosts = async (
        uuid: string,
    ): Promise<{ scheduledCount: number; draftCount: number; totalCount: number }> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.socialScheduler.affectedPosts(uuid),
        );
        return response?.data || response;
    };

    const pauseAccount = async (
        uuid: string,
    ): Promise<{ success: boolean; postsOnHold?: number }> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.POST,
            routes.socialScheduler.pauseAccount(uuid),
        );
        return response?.data || response;
    };

    const resumeAccount = async (
        uuid: string,
    ): Promise<{ success: boolean }> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.POST,
            routes.socialScheduler.resumeAccount(uuid),
        );
        return response?.data || response;
    };

    // ── Meta OAuth ─────────────────────────────────────────────

    const getMetaOAuthStatus = async (): Promise<{ configured: boolean }> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.socialScheduler.metaOAuthStatus,
        );
        return response?.data || response;
    };

    const startMetaOAuth = async (): Promise<{ authUrl: string }> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.socialScheduler.metaOAuthStart,
        );
        return response?.data || response;
    };

    // ───────────────────────────────────────────────────────────
    // Posts
    // ───────────────────────────────────────────────────────────

    const listPosts = async (params?: {
        accountUuid?: string;
        platform?: string;
        status?: string;
        search?: string;
        page?: number;
        limit?: number;
    }): Promise<SocialPostsListResponse> => {
        const qs = new URLSearchParams();
        if (params?.accountUuid) qs.set('accountUuid', params.accountUuid);
        if (params?.platform) qs.set('platform', params.platform);
        if (params?.status) qs.set('status', params.status);
        if (params?.search) qs.set('search', params.search);
        if (params?.page) qs.set('page', String(params.page));
        if (params?.limit) qs.set('limit', String(params.limit));
        const suffix = qs.toString() ? `?${qs.toString()}` : '';
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.socialScheduler.listPosts + suffix,
        );
        return response?.data || response;
    };

    const getPost = async (uuid: string): Promise<SocialPost> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.socialScheduler.getPost(uuid),
        );
        return response?.data || response;
    };

    const createPost = async (
        payload: CreateSocialPostPayload,
    ): Promise<SocialPost> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.POST,
            routes.socialScheduler.createPost,
            payload,
        );
        return response?.data || response;
    };

    const updatePost = async (
        uuid: string,
        payload: UpdateSocialPostPayload,
    ): Promise<SocialPost> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.PATCH,
            routes.socialScheduler.updatePost(uuid),
            payload,
        );
        return response?.data || response;
    };

    const deletePost = async (
        uuid: string,
    ): Promise<{ success: boolean }> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.DELETE,
            routes.socialScheduler.deletePost(uuid),
        );
        return response?.data || response;
    };

    const schedulePost = async (uuid: string): Promise<SocialPost> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.POST,
            routes.socialScheduler.schedulePost(uuid),
        );
        return response?.data || response;
    };

    const unschedulePost = async (uuid: string): Promise<SocialPost> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.POST,
            routes.socialScheduler.unschedulePost(uuid),
        );
        return response?.data || response;
    };

    const getCalendarPosts = async (params: {
        startDate: string;
        endDate: string;
    }): Promise<SocialPost[]> => {
        const qs = new URLSearchParams();
        qs.set('startDate', params.startDate);
        qs.set('endDate', params.endDate);
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.socialScheduler.calendarPosts + `?${qs.toString()}`,
        );
        return response?.data || response || [];
    };

    // ───────────────────────────────────────────────────────────
    // AI Caption Generation (SSE)
    // ───────────────────────────────────────────────────────────

    const generateCaptionStream = (
        payload: GenerateCaptionPayload,
        onEvent: (event: { type: string; data: any }) => void,
    ): AbortController => {
        const controller = new AbortController();

        (async () => {
            try {
                const token = AuthClientStore.getAccessToken();
                const baseUrl = process.env.REACT_APP_PMS_API_URL || 'http://localhost:8082';
                const response = await fetch(
                    `${baseUrl}${routes.socialScheduler.generateCaption}`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify(payload),
                        signal: controller.signal,
                    },
                );

                if (!response.body) throw new Error('No response body');
                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    buffer += decoder.decode(value, { stream: true });

                    const lines = buffer.split('\n');
                    buffer = lines.pop() || '';

                    for (const line of lines) {
                        if (!line.startsWith('data: ')) continue;
                        const chunk = line.slice(6).trim();
                        if (chunk === '[DONE]') {
                            onEvent({ type: 'done', data: {} });
                            return;
                        }
                        try {
                            const parsed = JSON.parse(chunk);
                            onEvent(parsed);
                        } catch {
                            // Ignore malformed chunks
                        }
                    }
                }
            } catch (err: any) {
                if (err.name !== 'AbortError') {
                    onEvent({ type: 'error', data: { message: err.message } });
                }
            }
        })();

        return controller;
    };

    // ───────────────────────────────────────────────────────────
    // Metrics & Dashboard
    // ───────────────────────────────────────────────────────────

    const getPostMetrics = async (uuid: string): Promise<SocialPostMetric | null> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.socialScheduler.getPostMetrics(uuid),
        );
        return response?.data || response;
    };

    const getPostMetricsHistory = async (uuid: string): Promise<SocialPostMetric[]> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.socialScheduler.getPostMetricsHistory(uuid),
        );
        return response?.data || response || [];
    };

    const getDashboard = async (params?: {
        startDate?: string;
        endDate?: string;
    }): Promise<SocialDashboardMetrics> => {
        const qs = new URLSearchParams();
        if (params?.startDate) qs.set('startDate', params.startDate);
        if (params?.endDate) qs.set('endDate', params.endDate);
        const suffix = qs.toString() ? `?${qs.toString()}` : '';
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.socialScheduler.dashboard + suffix,
        );
        return response?.data || response;
    };

    return {
        // Accounts
        listAccounts,
        getAccount,
        connectAccount,
        updateAccount,
        disconnectAccount,
        getAffectedPosts,
        pauseAccount,
        resumeAccount,
        getMetaOAuthStatus,
        startMetaOAuth,
        // Posts
        listPosts,
        getPost,
        createPost,
        updatePost,
        deletePost,
        schedulePost,
        unschedulePost,
        getCalendarPosts,
        // AI Caption
        generateCaptionStream,
        // Metrics & Dashboard
        getPostMetrics,
        getPostMetricsHistory,
        getDashboard,
    };
};
